import { Button } from '@/client/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/client/components/ui/dialog';
import { trpc } from '@/client/utils/trpc';
import type { RouterOutputs } from '@/client/utils/trpc';
import { useEffect, useState } from 'react';
import { EntryFormFields, emptyEntryForm } from './EntryFormFields';
import type { EntryForm } from './EntryFormFields';

type Entry = RouterOutputs['timesheets']['listEntries'][number];

interface EntryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: Entry | null;
  projects: Array<{ id: string; name: string }>;
  onSubmit: (form: EntryForm) => void;
  isPending: boolean;
  error: string | null;
  defaultDate?: string;
  onDelete?: () => void;
  isDeleting?: boolean;
  isAdmin?: boolean;
}

export function EntryDialog({
  open,
  onOpenChange,
  entry,
  projects,
  onSubmit,
  isPending,
  error,
  defaultDate,
  onDelete,
  isDeleting,
  isAdmin = false,
}: EntryDialogProps) {
  const [form, setForm] = useState<EntryForm>(emptyEntryForm());

  useEffect(() => {
    if (open) {
      setForm(
        entry
          ? {
              project_id: entry.project_id,
              date: entry.date,
              description: entry.description,
              hours: String(entry.hours),
            }
          : { ...emptyEntryForm(), ...(defaultDate ? { date: defaultDate } : {}) },
      );
    }
  }, [open, entry, defaultDate]);

  const showRecent = isAdmin && !entry;
  const { data: recentEntries = [] } = trpc.timesheets.recentProjectEntries.useQuery(
    { project_id: form.project_id },
    { enabled: showRecent && !!form.project_id },
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(form);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={showRecent && recentEntries.length > 0 ? 'max-w-2xl' : undefined}>
        <DialogHeader>
          <DialogTitle>{entry === null ? 'Add Entry' : 'Edit Entry'}</DialogTitle>
        </DialogHeader>
        <div className="flex gap-6">
          <form onSubmit={handleSubmit} className="flex-1 space-y-4 min-w-0">
            <EntryFormFields form={form} setForm={setForm} projects={projects} />
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex items-center justify-between">
              {onDelete ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={onDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </Button>
              ) : (
                <span />
              )}
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? 'Saving…' : entry === null ? 'Add' : 'Save'}
                </Button>
              </div>
            </div>
          </form>

          {showRecent && recentEntries.length > 0 && (
            <div className="w-52 shrink-0">
              <p className="mb-2 text-sm font-medium text-muted-foreground">Recent entries</p>
              <div className="max-h-80 space-y-1 overflow-y-auto">
                {recentEntries.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        date: r.date,
                        description: r.description,
                        hours: String(r.hours),
                      }))
                    }
                    className="w-full rounded-md border px-3 py-2 text-left text-xs transition-colors hover:bg-accent"
                  >
                    <div className="mb-0.5 flex items-center justify-between gap-1">
                      <span className="truncate font-medium text-muted-foreground">
                        {r.person_name.split(' ')[0]}
                      </span>
                      <span className="tabular-nums font-semibold">{r.hours}h</span>
                    </div>
                    <div className="truncate">{r.description}</div>
                    <div className="mt-0.5 text-muted-foreground">{r.date.slice(5).replace('-', '/')}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
